<template>
    <div class="search-match-list">
        <button
            ref="countButton"
            type="button"
            class="search-match-list__count"
            :class="{ 'search-match-list__count--open': open }"
            :disabled="!matches.length"
            :aria-label="`${label}, ${$t('editor.text.showAllMatches')}`"
            :aria-expanded="open"
            @click="open = !open"
        >
            <span>{{ label }}</span>
            <IconChevronOutline
                v-if="matches.length"
                class="search-match-list__chevron"
                direction="down"
                aria-hidden="true"
            />
        </button>
        <div v-if="open && matches.length" class="search-match-list__popover">
            <div class="search-match-list__header">
                <span class="search-match-list__title">{{ title }}</span>
                <span class="search-match-list__hint">{{ $t('editor.text.matchListHint') }}</span>
            </div>
            <div ref="body" class="search-match-list__body" @scroll="onScroll">
                <button
                    v-for="(row, i) in rows"
                    :key="row.key"
                    type="button"
                    class="search-match-list__row"
                    :class="{ 'search-match-list__row--current': i === index }"
                    :aria-current="i === index ? 'true' : undefined"
                    @click="select(i)"
                >
                    <span class="search-match-list__location">{{ row.location }}</span>
                    <!-- prettier-ignore -->
                    <span class="search-match-list__snippet">{{ row.snippet.pre }}<mark v-if="row.snippet.hit">{{ row.snippet.hit }}</mark>{{ row.snippet.post }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { IconChevronOutline } from '@speechki-frontend/shared/ui/icons';

import { makeSnippet } from '../TextSearch/searchMatches';

export default {
    name: 'SearchMatchList',
    components: { IconChevronOutline },
    props: {
        matches: { type: Array, default: () => [] },
        index: { type: Number, default: -1 },
        query: { type: String, default: '' },
    },
    emits: ['select'],
    data() {
        return { open: false, limit: 200 };
    },
    computed: {
        countLabel() {
            return this.$t('editor.text.matchCount', this.matches.length, {
                n: this.matches.length,
            });
        },
        label() {
            if (!this.matches.length) return this.$t('editor.text.noMatches');
            return this.index < 0
                ? this.countLabel
                : this.$t('editor.text.matchPosition', {
                      current: this.index + 1,
                      total: this.matches.length,
                  });
        },
        title() {
            return this.$t('editor.text.matchListTitle', {
                count: this.countLabel,
                query: this.query,
            });
        },
        rows() {
            return this.matches.slice(0, this.limit).map((match) => ({
                key: match.key,
                location: this.$t(
                    match.option.sentence_number == null
                        ? 'editor.text.matchLocationPage'
                        : 'editor.text.matchLocation',
                    {
                        page: match.option.page,
                        sentence: match.option.sentence_number,
                    }
                ),
                snippet: makeSnippet(match.option.raw_text, match.start, match.length),
            }));
        },
    },
    watch: {
        open(open) {
            if (open) {
                this.limit = Math.max(200, this.index + 50);
                document.addEventListener('pointerdown', this.onPointerDown, true);
                document.addEventListener('keydown', this.onKeyDown, true);
                this.$nextTick(() => {
                    this.$refs.body
                        ?.querySelector('[aria-current="true"]')
                        ?.scrollIntoView({ block: 'nearest' });
                });
            } else {
                this.removeListeners();
            }
        },
        matches(matches) {
            this.limit = 200;
            if (this.$refs.body) this.$refs.body.scrollTop = 0;
            if (!matches.length) this.close();
        },
        query(query) {
            if (query.length < 3) this.close();
        },
    },
    beforeUnmount() {
        this.removeListeners();
    },
    methods: {
        removeListeners() {
            document.removeEventListener('pointerdown', this.onPointerDown, true);
            document.removeEventListener('keydown', this.onKeyDown, true);
        },
        onPointerDown(event) {
            if (!event.composedPath().includes(this.$el)) this.close();
        },
        onKeyDown(event) {
            if (event.key !== 'Escape') return;
            const input = this.$el.parentElement.querySelector('.search-suggest__input');
            this.closeWithFocus(document.activeElement === input);
        },
        closeWithFocus(fromInput = false) {
            const restoreFocus = fromInput || this.$el.contains(document.activeElement);
            this.close();
            if (restoreFocus) this.$refs.countButton.focus();
        },
        onScroll(event) {
            const { scrollHeight, scrollTop, clientHeight } = event.target;
            if (scrollHeight - scrollTop - clientHeight <= 200) {
                this.limit = Math.min(this.matches.length, this.limit + 200);
            }
        },
        close() {
            this.open = false;
        },
        select(index) {
            this.closeWithFocus();
            this.$emit('select', index);
        },
    },
};
</script>

<style lang="scss" src="./SearchMatchList.scss"></style>
